import React, { useState } from 'react';
import './HelpCenterPage.css';

interface Section {
  id: string;
  icon: string;
  title: string;
  content: React.ReactNode;
}

const sections: Section[] = [
  {
    id: 'overview',
    icon: '🛡️',
    title: 'What is AssureX?',
    content: (
      <div>
        <p>
          <strong>AssureX</strong> is an enterprise test management platform designed to help QA teams
          organize, execute, and track software testing across projects. It provides a structured
          workspace for managing test cases, test scenarios, and test runs — all in one place.
        </p>
        <ul>
          <li>Organize test assets in a hierarchical folder structure</li>
          <li>Create and manage test cases and test scenarios independently</li>
          <li>Run tests and capture execution results with evidence</li>
          <li>Export test data and share folders with stakeholders</li>
          <li>Integrate with CI/CD pipelines via S3 XML snapshots</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'projects',
    icon: '📁',
    title: 'Projects',
    content: (
      <div>
        <p>Projects are the top-level containers in AssureX. Each project represents a product, application, or testing initiative.</p>
        <h4>How to use Projects</h4>
        <ul>
          <li><strong>Create a Project</strong> — Click <em>Add New Project</em> on the Projects screen. Enter a name, optional description, and upload an icon.</li>
          <li><strong>Open a Project</strong> — Click any project card to navigate into its dashboard.</li>
          <li><strong>Edit a Project</strong> — Click the pencil icon on the project card to update its name, description, or icon.</li>
          <li><strong>Delete a Project</strong> — Click the trash icon. This permanently deletes the project and all its subfolders and test data.</li>
        </ul>
        <div className="help-tip">
          💡 Projects are shared across your organization. All team members with access can view and edit them.
        </div>
      </div>
    ),
  },
  {
    id: 'test-cases',
    icon: '✅',
    title: 'Test Cases',
    content: (
      <div>
        <p>Test Cases represent individual, step-by-step verification procedures. They are organized in a folder tree under the <strong>Test Cases</strong> section.</p>
        <h4>Folder Structure</h4>
        <ul>
          <li>Root folders (level 0) are project-level containers — they show statistics only.</li>
          <li>Subfolders hold the actual test cases and can be nested to any depth.</li>
        </ul>
        <h4>Creating Test Cases</h4>
        <ul>
          <li>Select a subfolder in the left tree, then click <em>Create Test Case</em>.</li>
          <li>Fill in: Description, Priority (Critical / High / Medium / Low), Preconditions, Steps, Expected Result, and Tags.</li>
        </ul>
        <h4>Importing Test Cases</h4>
        <ul>
          <li>Click <em>Import</em> in the action bar to upload an Excel (.xlsx) or TestRail XML file.</li>
          <li>XML imports automatically create subfolders matching the source structure.</li>
        </ul>
        <h4>Exporting Test Cases</h4>
        <ul>
          <li>Select one or more test cases using the checkboxes, then click <em>Export</em>.</li>
          <li>Downloads as an Excel file.</li>
        </ul>
        <h4>Bulk Delete</h4>
        <ul>
          <li>Select test cases and click <em>Delete Selected</em> to remove multiple at once.</li>
        </ul>
        <h4>Bulk Edit</h4>
        <ul>
          <li>Select 2 or more test cases and click <em>Bulk Edit</em> in the action bar.</li>
          <li>Change Priority and/or Tags (add or remove) across all selected items at once.</li>
          <li>Only fields you explicitly change get updated — blank fields are left as-is.</li>
        </ul>
        <h4>View Detail</h4>
        <ul>
          <li>Click any Test Case ID (e.g. <code>TC-12345</code>) in the table to open a read-only detail view.</li>
        </ul>
        <div className="help-tip">
          💡 Use the <strong>Columns</strong> button to show/hide columns like Preconditions, Steps, Expected Result, and Created By.
        </div>
      </div>
    ),
  },
  {
    id: 'test-scenarios',
    icon: '🔬',
    title: 'Test Scenarios',
    content: (
      <div>
        <p>Test Scenarios are higher-level descriptions of what needs to be tested, often covering end-to-end flows. They are managed independently from Test Cases.</p>
        <h4>Creating Scenarios</h4>
        <ul>
          <li>Navigate to <strong>Test Scenarios</strong> in the sidebar.</li>
          <li>Select a folder, then click <em>Create Scenario</em>.</li>
          <li>Fill in: Title, Priority (Critical / High / Medium / Low), Classification Tags, Regression (Yes/No), and AI Generated (Yes/No).</li>
          <li>Classification Tags are optional — <strong>Manual</strong> is pre-selected by default.</li>
          <li>Regression and AI Generated default to <strong>No</strong> but must be explicitly confirmed.</li>
        </ul>
        <h4>AI Generation</h4>
        <ul>
          <li>Click <em>Generate with AI</em> to automatically generate scenarios from a description using AI.</li>
          <li>Review the generated scenarios, select the ones you want, and save them to the folder.</li>
        </ul>
        <h4>Scenario IDs</h4>
        <ul>
          <li>Each scenario gets a unique ID (e.g. <code>SC-12345</code>) automatically assigned.</li>
          <li>Click the ID in the table to open a read-only detail view.</li>
        </ul>
        <h4>Bulk Edit</h4>
        <ul>
          <li>Select 2 or more scenarios and click <em>Bulk Edit</em> in the action bar.</li>
          <li>Change Priority, Environment, and/or Tags across all selected items at once.</li>
        </ul>
        <h4>Import / Export</h4>
        <ul>
          <li>Import scenarios from Excel using the <em>Import Excel</em> button.</li>
          <li>Select scenarios and click <em>Export</em> to download as Excel.</li>
        </ul>
        <div className="help-tip">
          💡 Scenarios and Test Cases are separate modules with their own folder trees. A scenario folder is independent of a test case folder.
        </div>
      </div>
    ),
  },
  {
    id: 'test-runs',
    icon: '▶️',
    title: 'Test Runs',
    content: (
      <div>
        <p>Test Runs let you execute a set of test cases or scenarios and record pass/fail results with evidence.</p>
        <h4>Creating a Test Run</h4>
        <ul>
          <li>Click <em>Create Test Run</em> from the action bar in Test Cases or Test Scenarios.</li>
          <li>Or navigate to <strong>Test Runs</strong> → <em>Create Test Run</em>.</li>
          <li>Select the source folder, run type (Test Case or Scenario), environment, and run name.</li>
        </ul>
        <h4>Executing a Test Run</h4>
        <ul>
          <li>Open a test run to see all items listed.</li>
          <li>For each item, set the result: <strong>Pass</strong>, <strong>Fail</strong>, or <strong>Blocked</strong>.</li>
          <li>Upload evidence screenshots or files for failed items.</li>
          <li>Add notes or comments per test item.</li>
        </ul>
        <h4>Evidence Storage</h4>
        <ul>
          <li>Evidence files are stored in S3 under a structured path: <code>Test Execution / Environment / Run Type / Project / Subfolder / Run Name / filename</code>.</li>
          <li>Files are named using the test case/scenario ID with a sequence number (e.g. <code>TC-12345_1.jpg</code>).</li>
        </ul>
        <div className="help-tip">
          💡 Test run results are tracked over time — you can view historical runs to monitor quality trends.
        </div>
      </div>
    ),
  },
  {
    id: 'folders',
    icon: '🗂️',
    title: 'Folder Management',
    content: (
      <div>
        <p>Both Test Cases and Test Scenarios use a hierarchical folder tree to organize content.</p>
        <h4>Creating Subfolders</h4>
        <ul>
          <li>Select a folder in the tree, then click <em>Create Subfolder</em>.</li>
          <li>Subfolders can be nested to any depth.</li>
        </ul>
        <h4>Renaming Folders</h4>
        <ul>
          <li>Right-click a folder in the tree to open the context menu, then select <em>Rename</em>.</li>
        </ul>
        <h4>Moving Folders</h4>
        <ul>
          <li>Right-click a folder and select <em>Move to...</em> to move it under a different parent folder.</li>
          <li>A picker shows all valid destination folders — you cannot move a folder into itself or its own children.</li>
          <li>Moving a folder updates the S3 Test Data XML paths automatically.</li>
        </ul>
        <h4>Deleting Folders</h4>
        <ul>
          <li>Deleting a folder removes all its subfolders and test content. This action cannot be undone.</li>
        </ul>
        <h4>Collapsing the Sidebar</h4>
        <ul>
          <li>Click the <strong>‹</strong> toggle button on the sidebar edge to collapse it and gain more screen space.</li>
        </ul>
        <h4>Share Link</h4>
        <ul>
          <li>Click <em>Share Link</em> in the action bar to generate a human-readable URL for a folder.</li>
          <li>The link includes the full folder path (e.g. <code>/test-scenarios/Campaigns/CB-34?folder_id=...</code>).</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'peer-review',
    icon: '🔍',
    title: 'Peer Review Process',
    content: (
      <div>
        <p>AssureX includes a mandatory peer review workflow for Test Scenario and Test Case folders before they can be used in Test Runs.</p>
        <h4>Review States</h4>
        <ul>
          <li><strong>Draft</strong> — Default state when a folder is created.</li>
          <li><strong>In Review</strong> — Submitted for review, awaiting reviewer action.</li>
          <li><strong>Approved</strong> — Reviewed and approved. Test Runs can now be created from this folder.</li>
          <li><strong>Changes Requested</strong> — Reviewer has requested changes. Submitter must fix and re-submit.</li>
        </ul>
        <h4>Submitting for Review</h4>
        <ul>
          <li>Right-click a folder in the tree and select <em>Submit for Review</em>.</li>
          <li>Enter the reviewer's email address and submit.</li>
          <li>The reviewer and all Admins receive an email notification with a direct link to the folder.</li>
        </ul>
        <h4>Reviewing a Folder</h4>
        <ul>
          <li>The assigned reviewer sees the folder in their <strong>Notifications</strong> panel under <em>In Review</em>.</li>
          <li>Right-click the folder and select <em>Review</em> to open the review panel.</li>
          <li>Complete the verification checklist, add a comment, then click <strong>Approve</strong> or <strong>Request Changes</strong>.</li>
          <li>The submitter and all Admins receive an email with the decision and any comments.</li>
        </ul>
        <h4>After Changes Requested</h4>
        <ul>
          <li>The folder appears in the submitter's <em>In Review</em> notification tab with a red "Changes Requested" badge.</li>
          <li>Fix the scenarios/test cases, then right-click the folder and <em>Submit for Review</em> again.</li>
        </ul>
        <h4>Test Run Gate</h4>
        <ul>
          <li>Only <strong>Approved</strong> folders appear in the Test Run creation dropdowns.</li>
          <li>The <em>Create Test Run</em> button is disabled for non-approved folders.</li>
        </ul>
        <div className="help-tip">
          💡 The review status badge is visible in the breadcrumb bar when a folder is selected — green = Approved, blue = In Review, red = Changes Requested.
        </div>
      </div>
    ),
  },
  {
    id: 'notifications',
    icon: '🔔',
    title: 'Notifications',
    content: (
      <div>
        <p>The notification bell (🔔) in the header shows your pending and completed review items.</p>
        <h4>In Review Tab</h4>
        <ul>
          <li>Shows folders assigned to <strong>you as reviewer</strong> that are waiting for your action.</li>
          <li>Also shows folders you submitted that have <strong>Changes Requested</strong> — these need your attention.</li>
          <li>Click <em>Review →</em> to navigate directly to the folder.</li>
          <li>The badge count on the bell shows how many items need your attention.</li>
        </ul>
        <h4>Completed Tab</h4>
        <ul>
          <li>Shows folders you submitted or reviewed that have been <strong>Approved</strong>.</li>
          <li>Click <em>Open →</em> to navigate to the folder.</li>
        </ul>
        <div className="help-tip">
          💡 Notifications auto-refresh every 60 seconds. Click the ↻ button to refresh manually.
        </div>
      </div>
    ),
  },
  {
    id: 'columns',
    icon: '⚙️',
    title: 'Column Selector',
    content: (
      <div>
        <p>Both Test Cases and Test Scenarios tables support customizable columns.</p>
        <ul>
          <li>Click the <strong>Columns</strong> button in the controls bar to open the column selector.</li>
          <li>Toggle any column on or off. Required columns (ID, Title, Actions) cannot be hidden.</li>
          <li>Your column preferences are saved in the browser and persist across sessions.</li>
        </ul>
        <h4>Available Columns</h4>
        <ul>
          <li><strong>ID</strong> — Unique test case/scenario identifier</li>
          <li><strong>Title / Description</strong> — The test name</li>
          <li><strong>Priority</strong> — Critical, High, Medium, or Low</li>
          <li><strong>Tags</strong> — Labels like Regression, Smoke, Sanity</li>
          <li><strong>Preconditions</strong> — Setup requirements (Test Cases only)</li>
          <li><strong>Steps</strong> — Step-by-step instructions (Test Cases only)</li>
          <li><strong>Expected Result</strong> — What should happen</li>
          <li><strong>Created Date</strong> — When the item was created</li>
          <li><strong>Created By</strong> — Who created it</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'admin',
    icon: '🔐',
    title: 'Admin Console',
    content: (
      <div>
        <p>The Admin Console is available to users with the <strong>Admin</strong> role.</p>
        <h4>User Management</h4>
        <ul>
          <li>View all registered users in the system.</li>
          <li>Change user roles (Admin, QA Lead, Tester, Viewer).</li>
          <li>Deactivate or remove users.</li>
        </ul>
        <h4>Role Permissions</h4>
        <ul>
          <li><strong>Admin</strong> — Full access including user management and all CRUD operations.</li>
          <li><strong>QA Lead</strong> — Can create/edit/delete projects, folders, test cases, scenarios, and runs.</li>
          <li><strong>Tester</strong> — Can create and execute test runs; limited edit access.</li>
          <li><strong>Viewer</strong> — Read-only access to all content.</li>
        </ul>
        <div className="help-tip">
          💡 Only Admins can access the Admin Console. The link appears in the sidebar only when logged in as Admin.
        </div>
      </div>
    ),
  },
  {
    id: 'search',
    icon: '🔍',
    title: 'Search & Filter',
    content: (
      <div>
        <p>AssureX provides search at multiple levels:</p>
        <h4>Global Search (Header)</h4>
        <ul>
          <li>The search bar in the top header is for navigating across projects, tests, and documentation.</li>
        </ul>
        <h4>Table Search</h4>
        <ul>
          <li>Each Test Cases and Test Scenarios table has its own search bar.</li>
          <li>Searches across ID, title/description, expected result, and tags simultaneously.</li>
          <li>Results update in real time as you type.</li>
          <li>A <em>Filtered: N</em> badge appears showing how many results match.</li>
        </ul>
        <h4>Project Search</h4>
        <ul>
          <li>On the Projects screen, use the search bar to filter project cards by name or description.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'shortcuts',
    icon: '⚡',
    title: 'Tips & Shortcuts',
    content: (
      <div>
        <ul>
          <li>Press <kbd>Enter</kbd> in the tag input field to add a tag without clicking the button.</li>
          <li>Click a test case/scenario ID in the table to open its read-only detail view.</li>
          <li>Use the sidebar collapse button (<strong>‹ ›</strong>) to maximize the content area.</li>
          <li>Select 2+ items and click <em>Bulk Edit</em> to update Priority and Tags across all at once.</li>
          <li>Right-click any folder in the tree to access Create Subfolder, Rename, Move, Submit for Review, and Delete.</li>
          <li>The <em>Share Link</em> feature generates a human-readable URL — recipients are taken directly to that folder.</li>
          <li>Column preferences are saved per browser — each team member can have their own view.</li>
          <li>Importing a TestRail XML file automatically recreates the folder hierarchy from the source.</li>
          <li>Evidence files uploaded during test runs are automatically organized in S3 by environment, run type, and project.</li>
          <li>Test Data (scenarios and test cases) is automatically synced to S3 as XML after every create, update, or delete.</li>
          <li>After logout, Google will always show the account picker — you can switch accounts on every login.</li>
        </ul>
      </div>
    ),
  },
];

export const HelpCenterPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const current = sections.find(s => s.id === activeSection) || sections[0];

  return (
    <div className="help-center-page">
      <div className="help-sidebar">
        <div className="help-sidebar-header">
          <h2>Help Center</h2>
          <p>AssureX User Guide</p>
        </div>
        <nav className="help-nav">
          {sections.map(s => (
            <button
              key={s.id}
              className={`help-nav-item${activeSection === s.id ? ' active' : ''}`}
              onClick={() => setActiveSection(s.id)}
            >
              <span className="help-nav-icon">{s.icon}</span>
              <span>{s.title}</span>
            </button>
          ))}
        </nav>
      </div>

      <div className="help-content">
        <div className="help-content-header">
          <span className="help-content-icon">{current.icon}</span>
          <h1>{current.title}</h1>
        </div>
        <div className="help-content-body">
          {current.content}
        </div>
      </div>
    </div>
  );
};
